import { Badge, cx } from '@/components/ui';
import { withoutResting } from './paperDisplay';
import type { TradingProfile } from './types';

/**
 * 추천 프로필 탭. (17단계 → 2026-08-20 재설계)
 *
 * 🔴 세 프로필은 같은 종목을 고른다. 다른 것은 출구다
 *   2.1년 백테스트에서 하이리스크 전용 유니버스(진폭 8% 이상)가 어떤 출구로도
 *   음의 기댓값으로 나와 폐기했다. 지금은 진입 기준이 완전히 같고
 *   목표·손절 배수만 다르다. 같은 날 세 탭에 같은 종목이 뜨는 것이 정상이다.
 *   탭 아래 한 줄 설명이 그것을 말해준다.
 *
 * undefined 가 전체다. null 을 쓰지 않는 이유는 API 파라미터를
 * 생략하는 것과 뜻이 같아야 하기 때문이다.
 */

interface Props {
  value: TradingProfile | undefined;
  onChange: (profile: TradingProfile | undefined) => void;
  /** 프로필별 건수. 없으면 숫자를 그리지 않는다 */
  counts?: Partial<Record<TradingProfile | 'ALL', number>>;
  /**
   * 숨길 프로필 — 지금 회차에 쉬는 계좌(2026-10-10 사용자 결정 · 141번 10.6 · useRestingProfiles).
   * 오늘 추천 목록만 넘긴다. "지난 추천"(JournalPage)은 기록 화면이라 넘기지 않아 1 ~ 3회차 추천이 그대로 보인다.
   */
  hidden?: readonly TradingProfile[];
  /**
   * 중단 표식을 붙일 프로필 — 탭은 그대로 보이되 "중단" 이 붙는다(2026-10-10 사용자 "지난 추천에서는 두 탭을 보여 주되
   * 종료된 거라는 표식이 있었으면"). "지난 추천"(JournalPage)이 지금 쉬는 프로필을 넘긴다 — 3회차엔 비어 있어 표식이 없다.
   */
  ended?: readonly TradingProfile[];
}

const TABS: { key: TradingProfile | undefined; label: string }[] = [
  { key: undefined, label: '전체' },
  { key: 'SAFE', label: '🛡️ 안전' },
  { key: 'MODERATE', label: '⚖️ 분할익절' },
  { key: 'AGGRESSIVE', label: '⚡ 하이리스크' },
];

/** 그 프로필이 무엇을 하는지. 화면에서 기준을 숨기지 않는다 */
export const PROFILE_NOTE: Record<TradingProfile, string> = {
  SAFE: '자주 먹고 빨리 회전한다. 목표 +1.5 ATR · 손절 −1.0 ATR',
  MODERATE:
    '분할매도 — 절반을 +1.5 ATR 에서 익절하고 손절선을 본전으로 올린 뒤, 나머지가 +3.0 ATR 을 노린다. 손절 −1.5 ATR',
  AGGRESSIVE:
    '큰 꼬리를 노리고 오래 기다린다. 목표 +3.0 ATR · 손절 −1.5 ATR. 종목은 안전과 같다 — 다른 것은 출구다',
};

export function ProfileTabs({ value, onChange, counts, hidden = [], ended = [] }: Props) {
  return (
    <div>
      <div className="flex flex-wrap gap-1">
        {withoutResting(TABS, hidden).map((tab) => {
          const count = counts?.[tab.key ?? 'ALL'];
          const isEnded = tab.key !== undefined && ended.includes(tab.key);
          return (
            <button
              key={tab.key ?? 'ALL'}
              type="button"
              onClick={() => onChange(tab.key)}
              className={cx(
                'rounded-full px-3 py-1 text-xs font-medium',
                tab.key === value
                  ? 'bg-brand text-brand-fg'
                  : 'bg-surface-hover text-fg-muted',
              )}
            >
              {tab.label}
              {/* 중단된 계좌는 기록만 남는다 — 탭은 두고 표식으로 가른다(141번 10.6) */}
              {isEnded && <span className="ml-1 opacity-70">· 중단</span>}
              {count !== undefined && <span className="ml-1 tabular opacity-70">{count}</span>}
            </button>
          );
        })}
      </div>

      {value !== undefined && (
        <p className="mt-2 text-xs text-fg-subtle">
          {ended.includes(value) && '잠정 중단한 계좌입니다 — 새 추천은 없고, 아래는 중단 전까지의 기록입니다. '}
          {PROFILE_NOTE[value]}
        </p>
      )}
    </div>
  );
}

/**
 * 추천 카드에 붙는 프로필 배지.
 *
 * 전체 탭에서 두 기준이 섞여 보이므로 카드마다 어느 쪽인지 적어야 한다.
 * 목표가 +8% 와 +16% 가 같은 목록에 있는데 구분이 없으면
 * 낮은 쪽이 그냥 못한 추천으로 보인다.
 */
export function ProfileBadge({
  profile,
  label,
}: {
  profile: TradingProfile;
  label: string;
}) {
  return <Badge tone={profile === 'AGGRESSIVE' ? 'warning' : 'neutral'}>{label}</Badge>;
}
